<script lang="ts" setup>
import { useI18n } from 'vue-i18n';
import { getCurrentLocale } from '@/i18n';
import { capitalize } from 'lodash';
import { formatDate } from '@/utils/dateUtils';
import { getTextVariant } from '@/utils/stringUtils';
import { useConvertTextComposable } from '../useConvertTextComposable';
import { useRenderStatusComposable } from '../useRenderStatusComposable';

interface Props {
  ruleData: object;
  ruleView: any;
}
defineProps<Props>();

const { t } = useI18n({ useScope: 'global' });
const { renderClass, renderStatusYesNo, renderStatus } = useRenderStatusComposable();

const { handleReplaceValueToName } = useConvertTextComposable();
const currentLanguage = getCurrentLocale();
</script>
<template>
  <div class="tour-quote-rule">
    <div class="option">
      <div class="lead-option">{{ t('common.tour') }}</div>
      <div class="detail-option flex grid justify-content-between pr-4">
        <div class="col-4 mt-2">
          <div class="gateway font-bold mb-5 mt-2">
            <div class="flex gap-3">
              <div class="min-w-label">{{ t('common.gateway') }}:</div>
              <div class="font-normal">{{ ruleData.gate }} ({{ ruleData.gateCode }})</div>
            </div>
          </div>
          <div class="destination font-bold mb-2">
            <div class="flex gap-3">
              <div class="min-w-label">{{ t('common.destination') }}:</div>
              <div class="font-normal">{{ ruleData.dest }} ({{ ruleData.destCode }})</div>
            </div>
          </div>
        </div>
        <div class="col-4 mt-2">
          <div class="tourOptions font-bold mt-2 mb-2" v-for="(option, index) in ruleData.tourLog" :key="index">
            <div class="flex">
              <div class="min-w-label">{{ t('common.tour') }} {{ index + 1 }}:</div>
              <div class="font-normal">
                ({{ formatDate(option.depDate, 'yyyy-LLL-dd', true, currentLanguage) }}) {{ option.detail[`tourName${capitalize(currentLanguage)}`] }}
                <span v-if="option.detail[`tourInclusion${capitalize(currentLanguage)}`]">- {{ option.detail[`tourInclusion${capitalize(currentLanguage)}`] }} </span>( {{ option.detail[`tourDuration${capitalize(currentLanguage)}`] }} )
                <span v-if="option.detail.tourType">- {{ handleReplaceValueToName(option.detail.tourType, 'tourType') }}</span>
              </div>
            </div>
          </div>
        </div>
        <div class="mt-2">
          <div class="adults font-bold mt-2 mb-2">
            <div class="flex">
              <div class="min-w-label">{{ t('common.adults') }}:</div>
              <div class="font-normal">{{ ruleData.adtNo }}</div>
            </div>
          </div>
          <div class="child font-bold mb-2">
            <div class="flex">
              <div class="min-w-label">{{ t('common.children') }}:</div>
              <div class="font-normal">{{ ruleData.chdNo }}</div>
            </div>
          </div>
          <div class="total font-bold mb-2">
            <div class="flex">
              <div class="min-w-label">{{ t('worksheet.total_passengers') }}:</div>
              <div class="font-normal">{{ ruleData.adtNo + ruleData.chdNo }}</div>
            </div>
          </div>
          <div class="number_room font-bold mb-2">
            <div class="flex">
              <div class="min-w-label">{{ t('advance_quote.form.number_of_room') }}:</div>
              <div class="font-normal">{{ ruleData.numOfRooms }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="mt-2 ml-4">
      <ul>
        <li class="text-lg">
          {{ $t('rule.group_identification.title') }}
          <ul>
            <li class="text-lg mt-1 multiple_option">
              {{ $t('rule.group_identification.multiple_option') }} <span :class="renderClass(ruleView?.multiOptionForSamePassenger)">({{ renderStatusYesNo(ruleView?.multiOptionForSamePassenger) }})</span>
            </li>
            <li class="text-lg mt-1 travel_together">
              {{ $t('rule.group_identification.travel_together') }} <span :class="renderClass(ruleView?.optionTravellingTogetherAsGroup)">({{ renderStatusYesNo(ruleView?.optionTravellingTogetherAsGroup) }})</span>
            </li>
            <ul>
              <li class="text-lg mt-1 check_total">
                {{ $t('rule.group_identification.check_total') }} <span :class="renderClass(ruleView?.totalPassengerAgainstMaxRoom)">({{ renderStatus(ruleView?.totalPassengerAgainstMaxRoom) }})</span>
              </li>
            </ul>
            <ul>
              <li class="text-lg mt-1 same_across_all_option">
                {{ $t('rule.group_identification.same_across_all_option') }}
                <span :class="renderClass(ruleView?.gatewayDestinationDepartureDateSameAllOptions)">({{ renderStatusYesNo(ruleView?.gatewayDestinationDepartureDateSameAllOptions) }})</span>
              </li>
              <ul>
                <li class="text-lg mt-1 seats_request">
                  {{ $t('rule.group_identification.seats_request') }} <span :class="renderClass(ruleView?.totalSRLessThanOrEqualMaxSRFlightRule)">({{ renderStatus(ruleView?.totalSRLessThanOrEqualMaxSRFlightRule) }})</span>
                </li>
              </ul>
            </ul>
          </ul>
        </li>
        <li class="text-lg mt-2 group_type_selected">
          {{ $t('rule.group_type') }} <span>({{ getTextVariant(currentLanguage, ruleData.gType, ruleData.gTypeFr) }})</span>
          <span :class="renderClass(ruleView?.groupTypeSelectedAsInstaQuote)">({{ renderStatus(ruleView?.groupTypeSelectedAsInstaQuote) }})</span>
        </li>
        <li class="text-lg mt-2 brochure_code">
          {{ $t('rule.brochure_code') }}
          <span v-if="ruleData.brochure">({{ ruleData.brochure }})</span>
          <span :class="renderClass(ruleView?.brochureAvailable)">({{ renderStatus(ruleView?.brochureAvailable) }})</span>
        </li>
      </ul>
    </div>
  </div>
</template>
<style lang="scss" scoped>
:deep() {
  .min-w-240 {
    min-width: 240px !important;
    width: 100%;
  }
}
.lead-option {
  background-color: $col-primary;
  color: $col-white;
  padding: 1.05rem 1.7rem;
  margin-top: 1.4rem;
  font-weight: 700;
}

.min-w-label {
  min-width: 16rem;
}
</style>
